import { AccessModes, Dimensions, Metrics, SortOrder, Sources, WITH_ID_SUFFIX } from 'components/insights/constants';
import { InsightsQueryParams } from 'components/insights/query/useInsightsAnalytics';
import { ReviewType } from 'models/Review';

// --- Metric keys ---

export const NetworkPerformanceMetrics = {
  AVG_START_OF_CARE: 'avg_start_of_care',
  SERVICE_REFUSALS: 'service_refusals',
  SAFE_RETURNS: 'safe_returns',
  OES: 'oes',
  READMISSION_RATE: 'readmission_rate',
  ENGAGEMENTS: 'engagements',
} as const;

export type NetworkPerformanceMetricDrillKey =
  (typeof NetworkPerformanceMetrics)[keyof typeof NetworkPerformanceMetrics];

// --- Unified metric configuration (identity + query structure) ---

export type MetricConfig = {
  label: string;
  primaryMetricLabel: string;
  exportSlug: string;
  source: Sources;
  entityDimension: Dimensions;
  counterDimension: Dimensions;
  subDimensions: string[];
  primaryMetric: Metrics;
  additionalMetrics: Metrics[];
  reviewType?: ReviewType;
  exportMetrics: Metrics[];
};

const M = NetworkPerformanceMetrics;

export const METRIC_CONFIGS: Record<NetworkPerformanceMetricDrillKey, MetricConfig> = {
  [M.AVG_START_OF_CARE]: {
    label: 'Avg. Start of Care Time',
    primaryMetricLabel: 'Avg. Start of Care Time',
    exportSlug: 'avg-start-of-care',
    source: Sources.INSIGHTS_CARE_TRANSITIONS,
    entityDimension: Dimensions.PROVIDER_CLIENT,
    counterDimension: Dimensions.PREDECESSOR_GROUP,
    subDimensions: [Dimensions.GROUP],
    primaryMetric: Metrics.DAYS_TO_START_CARE_AVERAGE,
    additionalMetrics: [Metrics.LOCATION_EPISODE_ID_COUNT],
    exportMetrics: [Metrics.DAYS_TO_START_CARE_AVERAGE, Metrics.LOCATION_EPISODE_ID_COUNT],
  },
  [M.SERVICE_REFUSALS]: {
    label: 'Service Refusals',
    primaryMetricLabel: 'Total Service Refusals',
    exportSlug: 'service-refusals',
    source: Sources.REVIEWS,
    entityDimension: Dimensions.PROVIDER_CLIENT,
    counterDimension: Dimensions.PREDECESSOR_GROUP,
    subDimensions: [Dimensions.GROUP, Dimensions.SERVICE_REFUSAL_REFUSED_BY_TYPE],
    primaryMetric: Metrics.SERVICE_REFUSALS_COUNT,
    additionalMetrics: [],
    reviewType: ReviewType.SERVICE_REFUSAL,
    exportMetrics: [Metrics.SERVICE_REFUSALS_COUNT],
  },
  [M.SAFE_RETURNS]: {
    label: 'Safe Returns',
    primaryMetricLabel: 'Safe Returns Requested',
    exportSlug: 'safe-returns',
    source: Sources.REVIEWS,
    entityDimension: Dimensions.PROVIDER_CLIENT,
    counterDimension: Dimensions.PREDECESSOR_GROUP,
    subDimensions: [Dimensions.GROUP],
    primaryMetric: Metrics.SAFE_RETURNS_REQUESTED_COUNT,
    additionalMetrics: [Metrics.SAFE_RETURN_RESPONSE_TIME_AVERAGE],
    reviewType: ReviewType.SAFE_RETURN,
    exportMetrics: [Metrics.SAFE_RETURNS_REQUESTED_COUNT, Metrics.SAFE_RETURN_RESPONSE_TIME_AVERAGE],
  },
  [M.OES]: {
    label: 'Olio Engagement Score',
    primaryMetricLabel: 'Olio Engagement Score (OES)',
    exportSlug: 'olio-engagement-score',
    source: Sources.LOCATION_EPISODE_DISCHARGES,
    entityDimension: Dimensions.PROVIDER_CLIENT,
    counterDimension: Dimensions.PREDECESSOR_GROUP,
    subDimensions: [Dimensions.GROUP_NAME],
    primaryMetric: Metrics.OLIO_ENGAGEMENT_SCORE,
    additionalMetrics: [Metrics.ID_COUNT, Metrics.LENGTH_OF_STAY_AVERAGE],
    exportMetrics: [Metrics.OLIO_ENGAGEMENT_SCORE, Metrics.ID_COUNT, Metrics.LENGTH_OF_STAY_AVERAGE],
  },
  [M.READMISSION_RATE]: {
    label: 'Readmission Rate',
    primaryMetricLabel: 'Readmission Rate (%)',
    exportSlug: 'readmission-rate',
    source: Sources.LOCATION_EPISODE_DISCHARGES,
    entityDimension: Dimensions.PROVIDER_CLIENT,
    counterDimension: Dimensions.PREDECESSOR_GROUP,
    subDimensions: [Dimensions.GROUP_NAME],
    primaryMetric: Metrics.READMISSION_RATE,
    additionalMetrics: [Metrics.READMISSIONS_SUM, Metrics.ID_COUNT, Metrics.READMISSION_LENGTH_OF_STAY_AVERAGE],
    exportMetrics: [Metrics.READMISSION_RATE, Metrics.ID_COUNT, Metrics.READMISSIONS_SUM],
  },
  [M.ENGAGEMENTS]: {
    label: 'Engagements Per Patient Per Week',
    primaryMetricLabel: 'Engagements Per Patient Per Week',
    exportSlug: 'engagements-per-patient-per-week',
    source: Sources.LOCATION_EPISODE_DISCHARGES,
    entityDimension: Dimensions.PREDECESSOR_GROUP,
    counterDimension: Dimensions.PROVIDER_CLIENT,
    subDimensions: [],
    primaryMetric: Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK,
    additionalMetrics: [Metrics.ID_COUNT, Metrics.OWNER_ACTIVITIES_SUM],
    exportMetrics: [Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK, Metrics.OWNER_ACTIVITIES_SUM, Metrics.ID_COUNT],
  },
};

// --- Derived query params ---

export type SlideoutQueryParams = {
  params: InsightsQueryParams;
  hasSubRows: boolean;
  exportParams: InsightsQueryParams;
  counterParams: InsightsQueryParams;
};

export function getQueryParams(metric: NetworkPerformanceMetricDrillKey): SlideoutQueryParams {
  const c = METRIC_CONFIGS[metric];
  const metrics = [c.primaryMetric, ...c.additionalMetrics];
  const base: InsightsQueryParams = {
    source: c.source,
    accessModes: [AccessModes.MANAGER],
    metrics,
    ...(c.reviewType ? { reviewType: c.reviewType } : {}),
  };

  return {
    params: {
      ...base,
      dimensions: [`${c.entityDimension}${WITH_ID_SUFFIX}`, ...c.subDimensions],
      sortBy: `${c.primaryMetric} ${SortOrder.DESC}`,
      rollups: true,
    },
    hasSubRows: c.subDimensions.length > 0,
    exportParams: {
      ...base,
      metrics: c.exportMetrics,
      dimensions: [c.entityDimension, ...c.subDimensions.map((d) => d.replace(WITH_ID_SUFFIX, ''))],
    },
    counterParams: {
      source: c.source,
      accessModes: [AccessModes.MANAGER],
      dimensions: [c.counterDimension],
      metrics: [c.primaryMetric],
      ...(c.reviewType ? { reviewType: c.reviewType } : {}),
    },
  };
}
